/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Disposable, DisposableStore, IDisposable } from '../../../base/common/lifecycle.js';
import { equals } from '../../../base/common/objects.js';
import { autorun, IObservable, IReader } from '../../../base/common/observable.js';
import { hasKey } from '../../../base/common/types.js';
import { URI } from '../../../base/common/uri.js';
import { generateUuid } from '../../../base/common/uuid.js';
import { localize } from '../../../nls.js';
import { IInstantiationService } from '../../instantiation/common/instantiation.js';
import { ILogService } from '../../log/common/log.js';
import { AgentSignal, IAgent, IAgentToolPendingConfirmationSignal } from '../common/agentService.js';
import { IAgentHostChangesetService } from './agentHostChangesetService.js';
import { IAgentHostCheckpointService } from '../common/agentHostCheckpointService.js';

import { ISessionDataService } from '../common/sessionDataService.js';
import { SessionConfigKey } from '../common/sessionConfigKeys.js';
import { ToolCallContributorKind, type AgentInfo } from '../common/state/protocol/state.js';
import { ActionType, StateAction, type SessionToolCallCompleteAction } from '../common/state/sessionActions.js';
import {
	buildSubagentSessionUri,
	getToolFileEdits,
	MessageKind,
	PendingMessageKind,
	ResponsePartKind,
	ROOT_STATE_URI,
	SessionStatus,
	ToolCallStatus,
	ToolResultContentType,
	type URI as ProtocolURI,
	type ErrorInfo,
	type SessionState,
	type ToolResultContent
} from '../common/state/sessionState.js';
import { AgentHostStateManager } from './agentHostStateManager.js';
import { parseRenameCommand } from './agentHostRenameCommand.js';
import { SessionPermissionManager } from './sessionPermissions.js';
import { stripProxyErrorMarker, toChatErrorMeta, tryParseForwardedChatError } from './shared/forwardedChatError.js';
import { ITelemetryService } from '../../telemetry/common/telemetry.js';
import { updateAgentHostTelemetryLevelFromConfig } from './agentHostTelemetryService.js';
import { AgentHostTelemetryReporter } from './agentHostTelemetryReporter.js';
import { AgentHostTurnTracker } from './agentHostTurnTracker.js';
import { AgentHostSessionTitleController } from './agentHostSessionTitleController.js';
import type { ICopilotApiService } from './shared/copilotApiService.js';

/**
 * Options for constructing an {@link AgentSideEffects} instance.
 */
export interface IAgentSideEffectsOptions {
	/** Resolve the agent responsible for a given session URI. */
	readonly getAgent: (session: ProtocolURI) => IAgent | undefined;
	/** Observable set of registered agents. Triggers `root/agentsChanged` when it changes. */
	readonly agents: IObservable<readonly IAgent[]>;
	/** Session data service for cleaning up per-session data on disposal. */
	readonly sessionDataService: ISessionDataService;
	/** Get the GitHub token used for Copilot utility title generation. */
	readonly getGitHubCopilotToken?: () => string | undefined;
	/** CAPI service used for Copilot utility title generation. */
	readonly copilotApiService?: ICopilotApiService;
	/**
	 * Called after each top-level session turn completes so git state can be
	 * refreshed and published via `SessionMetaChanged`. Subagent turns are
	 * excluded — only the parent session URI is passed.
	 */
	readonly onTurnComplete: (session: ProtocolURI) => void;
}

/** A signal that was deferred because its subagent session does not exist yet. */
interface IPendingSubagentSignal {
	readonly signal: AgentSignal;
	readonly agent: IAgent;
}

/**
 * Shared implementation of agent side-effect handling.
 *
 * Routes client-dispatched actions to the correct agent backend,
 * restores sessions from previous lifetimes, handles filesystem
 * operations (browse/fetch/write), tracks pending permission requests,
 * and wires up agent progress events to the state manager.
 *
 * Session create/dispose/list and auth are handled by {@link AgentService}.
 */
export class AgentSideEffects extends Disposable {

	/** Maps tool call IDs to the agent that owns them, for routing confirmations. */
	private readonly _toolCallAgents = new Map<string, string>();
	private _lastAgentInfos: readonly AgentInfo[] = [];

	private readonly _permissionManager: SessionPermissionManager;

	/**
	 * Maps `parentSession:toolCallId` → subagent session URI.
	 * Used to route signals with `parentToolCallId` to the correct subagent.
	 */
	private readonly _subagentSessions = new Map<string, ProtocolURI>();

	/**
	 * Buffers signals whose `parentToolCallId` references a subagent
	 * whose `subagent_started` signal has not yet been processed. The SDK is
	 * not strict about ordering: an inner `tool_start` can arrive before the
	 * `subagent_started` that creates the child session. Without buffering,
	 * those signals would be dispatched against the parent session and the
	 * UI would render the inner tool calls flat at the top level rather than
	 * grouping them under the subagent. Drained by `_handleSubagentStarted`.
	 *
	 * Key: `${parentSession}:${parentToolCallId}`.
	 */
	private readonly _pendingSubagentSignals = new Map<string, IPendingSubagentSignal[]>();
	private readonly _telemetryReporter: AgentHostTelemetryReporter;
	private readonly _turnTracker: AgentHostTurnTracker;
	private readonly _titleController: AgentHostSessionTitleController;

	constructor(
		private readonly _stateManager: AgentHostStateManager,
		private readonly _options: IAgentSideEffectsOptions,
		@IInstantiationService instantiationService: IInstantiationService,
		@ILogService private readonly _logService: ILogService,
		@IAgentHostChangesetService private readonly _changesets: IAgentHostChangesetService,
		@ITelemetryService private readonly _telemetryService: ITelemetryService,
		@IAgentHostCheckpointService private readonly _checkpointService: IAgentHostCheckpointService,
	) {
		super();
		this._telemetryReporter = new AgentHostTelemetryReporter(this._telemetryService);
		this._turnTracker = new AgentHostTurnTracker(this._telemetryReporter);
		this._permissionManager = this._register(instantiationService.createInstance(SessionPermissionManager, this._stateManager));
		this._titleController = this._register(instantiationService.createInstance(AgentHostSessionTitleController, this._stateManager, {
			sessionDataService: this._options.sessionDataService,
			getGitHubCopilotToken: this._options.getGitHubCopilotToken,
			copilotApiService: this._options.copilotApiService,
		}));

		// Whenever the agents observable changes, publish to root state.
		this._register(autorun(reader => {
			const agents = this._options.agents.read(reader);
			this._publishAgentInfos(agents, reader);
		}));

		// Server-dispatched SessionToolCallComplete actions (e.g. from
		// the disconnect timeout in ProtocolServerHandler) bypass
		// handleAction, so the agent's SDK deferred never resolves.
		// Listen for these envelopes and notify the agent directly.
		this._register(this._stateManager.onDidEmitEnvelope(envelope => {
			if (!envelope.origin && envelope.action.type === ActionType.SessionToolCallComplete) {
				const action = envelope.action;
				const agent = this._options.getAgent(envelope.channel);
				agent?.onClientToolCallComplete(URI.parse(envelope.channel), action.toolCallId, action.result);
			}
		}));
	}

	/**
	 * Publishes agent descriptors using the last known model lists.
	 */
	private _publishAgentInfos(agents: readonly IAgent[], reader?: IReader): void {
		const infos: AgentInfo[] = agents.map(a => {
			const d = a.getDescriptor();
			const protectedResources = a.getProtectedResources();
			const models = reader ? a.models.read(reader) : a.models.get();
			const customizations = a.getCustomizations?.();
			return {
				provider: d.provider, displayName: d.displayName, description: d.description, models: models.map(m => ({
					id: m.id,
					provider: m.provider,
					name: m.name,
					maxContextWindow: m.maxContextWindow,
					supportsVision: m.supportsVision,
					policyState: m.policyState,
					configSchema: m.configSchema,
					_meta: m._meta,
				})),
				customizations: customizations?.length ? [...customizations] : undefined,
				protectedResources: protectedResources.length > 0 ? protectedResources : undefined,
			};
		});
		if (equals(this._lastAgentInfos, infos)) {
			return;
		}
		this._lastAgentInfos = infos;
		this._stateManager.dispatchServerAction(ROOT_STATE_URI, { type: ActionType.RootAgentsChanged, agents: infos });
	}

	private async _publishSessionCustomizations(agent: IAgent, session: ProtocolURI): Promise<void> {
		if (!agent.getSessionCustomizations) {
			return;
		}

		const customizations = await agent.getSessionCustomizations(URI.parse(session));
		this._stateManager.dispatchServerAction(session, {
			type: ActionType.SessionCustomizationsChanged,
			customizations: [...customizations],
		});
	}

	private _publishSessionCustomizationsSoon(agent: IAgent, session: ProtocolURI): void {
		void this._publishSessionCustomizations(agent, session).catch(err => {
			this._logService.error('[AgentSideEffects] getSessionCustomizations failed', err);
		});
	}

	private _publishSessionCustomizationsForAgent(agent: IAgent): void {
		for (const session of this._stateManager.getSessionUris()) {
			if (this._options.getAgent(session) === agent) {
				this._publishSessionCustomizationsSoon(agent, session);
			}
		}
	}

	private _publishAllSessionCustomizations(): void {
		for (const session of this._stateManager.getSessionUris()) {
			const agent = this._options.getAgent(session);
			if (agent) {
				this._publishSessionCustomizationsSoon(agent, session);
			}
		}
	}

	// ---- Initialization ----------------------------------------------------

	/**
	 * Initializes async resources (tree-sitter WASM) used for command
	 * auto-approval. Await this before any session events can arrive to
	 * guarantee that auto-approval checks are fully synchronous.
	 */
	initialize(): Promise<void> {
		return this._permissionManager.initialize();
	}

	// ---- Agent registration -------------------------------------------------

	/**
	 * Registers a progress-signal listener on the given agent so that
	 * {@link AgentSignal}s are routed/dispatched through the state manager.
	 * Returns a disposable that removes the listener.
	 */
	registerProgressListener(agent: IAgent): IDisposable {
		const disposables = new DisposableStore();
		disposables.add(agent.onDidSessionProgress(signal => {
			this._handleAgentSignal(agent, signal);
		}));
		if (agent.onDidCustomizationsChange) {
			disposables.add(agent.onDidCustomizationsChange(() => {
				this._publishAgentInfos(this._options.agents.get());
				this._publishSessionCustomizationsForAgent(agent);
			}));
		}
		return disposables;
	}

	/**
	 * Routes a single signal from `agent` to the correct session.
	 *
	 * Action signals with a `parentToolCallId` are routed to the matching
	 * subagent session. If the subagent session does not exist yet (the SDK
	 * can emit an inner `tool_start` before its `subagent_started`), the
	 * signal is buffered in {@link _pendingSubagentSignals} and replayed
	 * once the `subagent_started` arrives.
	 */
	private _handleAgentSignal(agent: IAgent, signal: AgentSignal): void {
		const sessionKey = signal.session.toString();

		// Track tool calls so handleAction can route confirmations. Defer
		// registration for inner subagent tool calls until we know which
		// subagent session they belong to — otherwise we'd register them
		// under the parent session key and a later `pending_confirmation`
		// (which lacks
		// `parentToolCallId`) could be routed against the wrong session.
		if (signal.kind === 'action'
			&& signal.action.type === ActionType.SessionToolCallStart
			&& !signal.parentToolCallId
		) {
			this._toolCallAgents.set(`${sessionKey}:${signal.action.toolCallId}`, agent.id);
		}

		if (signal.kind === 'subagent_started') {
			this._handleSubagentStarted(sessionKey, signal.toolCallId, signal.agentName, signal.agentDisplayName, signal.agentDescription);
			this._drainPendingSubagentSignals(sessionKey, signal.toolCallId);
			return;
		}

		if (signal.kind === 'subagent_completed') {
			this.completeSubagentSession(sessionKey, signal.toolCallId);
			return;
		}

		if (signal.kind === 'steering_consumed') {
			this._stateManager.dispatchServerAction(sessionKey, {
				type: ActionType.SessionPendingMessageRemoved,
				kind: PendingMessageKind.Steering,
				id: signal.id,
			});
			return;
		}

		// Route signals with parentToolCallId to the subagent session.
		// Both action signals and pending_confirmation signals can carry
		// a parentToolCallId — for client tools inside a subagent the
		// permission flow fires `pending_confirmation` for an inner tool
		// call, and that signal must be routed to the subagent session
		// (otherwise the resulting SessionToolCallReady would land on the
		// parent session, which has no matching SessionToolCallStart).
		const parentToolCallId = signal.kind === 'action' || signal.kind === 'pending_confirmation'
			? signal.parentToolCallId
			: undefined;
		if (parentToolCallId) {
			const subagentKey = `${sessionKey}:${parentToolCallId}`;
			const subagentSession = this._subagentSessions.get(subagentKey);
			if (subagentSession) {
				// Track tool calls in subagent context for confirmation routing.
				if (signal.kind === 'action' && signal.action.type === ActionType.SessionToolCallStart) {
					this._toolCallAgents.set(`${subagentSession}:${signal.action.toolCallId}`, agent.id);
				}
				const subTurnId = this._stateManager.getActiveTurnId(subagentSession);
				if (subTurnId) {
					this._dispatchActionForSession(signal, subagentSession, subTurnId, agent);
				}
				return;
			}

			// Subagent session does not exist yet — buffer the signal so we can
			// replay it after `subagent_started` arrives.
			this._logService.trace(`[AgentSideEffects] Buffering ${this._describeSignal(signal)} for pending subagent ${subagentKey}`);
			let buffer = this._pendingSubagentSignals.get(subagentKey);
			if (!buffer) {
				buffer = [];
				this._pendingSubagentSignals.set(subagentKey, buffer);
			}
			buffer.push({ signal, agent });
			return;
		}

		// Route pending_confirmation signals for tools inside subagent sessions
		// (legacy path for signals without an explicit parentToolCallId — the
		// tool was previously registered under its subagent session key in
		// _toolCallAgents).
		if (signal.kind === 'pending_confirmation') {
			const subagentSession = this._findSubagentSessionForToolCall(sessionKey, signal.state.toolCallId);
			if (subagentSession) {
				const subTurnId = this._stateManager.getActiveTurnId(subagentSession);
				if (subTurnId) {
					this._handleToolReady(signal, subagentSession, subTurnId, agent);
				}
				return;
			}
		}

		const turnId = this._stateManager.getActiveTurnId(sessionKey);
		if (turnId) {
			this._dispatchActionForSession(signal, sessionKey, turnId, agent);
			return;
		}

		// No active turn on the session. Non-action signals are silently
		// dropped, but action signals can still target session-level state
		// such as customizations, title, or configuration. A turnComplete
		// action also drives post-turn side effects even when the matching
		// turnStarted was not observed by this side-effects instance.
		if (signal.kind === 'action') {
			this._stateManager.dispatchServerAction(sessionKey, signal.action);
			if (signal.action.type === ActionType.SessionTurnComplete) {
				this._runTurnCompleteSideEffects(sessionKey, undefined);
			}
		}
	}

	/**
	 * Dispatches a signal against a resolved session+turn. Performs the
	 * subagent-content merge for tool_complete and the related side effects.
	 */
	private _dispatchActionForSession(signal: AgentSignal, sessionKey: ProtocolURI, turnId: string, agent?: IAgent): void {
		if (signal.kind === 'pending_confirmation') {
			if (agent) {
				this._handleToolReady(signal, sessionKey, turnId, agent);
			}
			return;
		}
		if (signal.kind !== 'action') {
			return;
		}
		// The agent emits actions with its own view of the active turnId
		// targeting the top-level session. The state manager is the source
		// of truth — rewrite `turnId` so the action lands in the right
		// reducer (queued turn ID when the agent hasn't yet seen
		// `sendMessage`, etc.). Routing to subagent sessions is handled by
		// the caller via the channel argument.
		// Actions without a `turnId` field (`SessionTitleChanged`,
		// `SessionInputRequested`) only get their channel rewritten.
		let action = signal.action;
		if (hasKey(action, { turnId: true }) && action.turnId !== turnId) {
			action = { ...action, turnId };
		}

		// When a parent tool call has an associated subagent session,
		// preserve the subagent content metadata in the completion result.
		// The SDK's tool_complete provides its own content which would
		// overwrite the ToolResultSubagentContent that was set via
		// SessionToolCallContentChanged while running.
		if (action.type === ActionType.SessionToolCallComplete) {
			const subagentKey = `${sessionKey}:${action.toolCallId}`;
			const subagentUri = this._subagentSessions.get(subagentKey);
			if (subagentUri) {
				const parentState = this._stateManager.getSessionState(sessionKey);
				const runningContent = this._getRunningToolCallContent(parentState, turnId, action.toolCallId);
				const subagentEntry = runningContent.find(c => hasKey(c, { type: true }) && c.type === ToolResultContentType.Subagent);
				if (subagentEntry) {
					const mergedContent = [...(action.result.content ?? []), subagentEntry];
					const merged: SessionToolCallCompleteAction = { ...action, result: { ...action.result, content: mergedContent } };
					action = merged;
				}
			}
		}

		this._stateManager.dispatchServerAction(sessionKey, action);

		// Mark first visible progress for TTFT telemetry
		if (action.type === ActionType.SessionDelta
			|| action.type === ActionType.SessionResponsePart
			|| action.type === ActionType.SessionToolCallStart
			|| action.type === ActionType.SessionReasoning) {
			this._turnTracker.markFirstProgress(sessionKey, turnId);
		}

		if (action.type === ActionType.SessionToolCallComplete) {
			// Drop any events that were buffered for a subagent whose
			// `subagent_started` never arrived (e.g. the parent tool failed
			// before the subagent was created). The actual subagent session
			// teardown is driven by the `subagent_completed` signal because
			// background subagents (`mode: background`) continue running
			// after the parent tool call returns.
			this._pendingSubagentSignals.delete(`${sessionKey}:${action.toolCallId}`);
			if (getToolFileEdits(action.result).length > 0) {
				this._changesets.onToolCallEditsApplied(sessionKey, turnId);
			}
		}

		if (action.type === ActionType.SessionTurnComplete) {
			this._turnTracker.turnCompleted(sessionKey, turnId, 'success');
			this._runTurnCompleteSideEffects(sessionKey, turnId);
		}

		if (action.type === ActionType.SessionTurnCancelled) {
			this._turnTracker.turnCompleted(sessionKey, turnId, 'cancelled');
		}

		if (action.type === ActionType.SessionError) {
			this._turnTracker.turnCompleted(sessionKey, turnId, 'error');
		}
	}

	/**
	 * Post-turn side effects: flush any pending debounced diff computation,
	 * compute final diffs immediately, drain the next queued message, and
	 * notify the host so it can refresh git state.
	 */
	private _runTurnCompleteSideEffects(sessionKey: ProtocolURI, turnId: string | undefined): void {
		// Capture the end-of-turn git checkpoint BEFORE notifying the
		// changeset service so the per-turn changeset recompute can take
		// the authoritative git-diff fast path (which includes terminal-tool
		// edits the FileEditTracker misses). The capture is best-effort —
		// any failure logs and the changeset pipeline falls back to the
		// `file_edits`-based path. We don't block subsequent side effects
		// (queued message drain, host notification) on the changeset
		// completion since those have always been fire-and-forget; the
		// ordering guarantee we care about is checkpoint-then-changeset.
		if (turnId !== undefined) {
			this._checkpointService.captureTurnCheckpoint(URI.parse(sessionKey), turnId).then(() => {
				this._changesets.onTurnComplete(sessionKey, turnId);
			}, err => {
				this._logService.warn(`[AgentSideEffects] Turn checkpoint capture failed for ${sessionKey}/${turnId}: ${err instanceof Error ? err.message : String(err)}`);
				this._changesets.onTurnComplete(sessionKey, turnId);
			});
		} else {
			this._changesets.onTurnComplete(sessionKey, turnId);
		}
		this._tryConsumeNextQueuedMessage(sessionKey);
		this._options.onTurnComplete(sessionKey);
	}

	private _describeSignal(signal: AgentSignal): string {
		return signal.kind === 'action' ? `action(${signal.action.type})` : signal.kind;
	}

	/**
	 * Replays any signals that were buffered while waiting for
	 * `subagent_started` to create the subagent session. Called immediately
	 * after `_handleSubagentStarted`.
	 */
	private _drainPendingSubagentSignals(parentSession: ProtocolURI, parentToolCallId: string): void {
		const subagentKey = `${parentSession}:${parentToolCallId}`;
		const buffer = this._pendingSubagentSignals.get(subagentKey);
		if (!buffer) {
			return;
		}
		this._pendingSubagentSignals.delete(subagentKey);
		this._logService.trace(`[AgentSideEffects] Draining ${buffer.length} buffered signal(s) for subagent ${subagentKey}`);
		for (const { signal, agent } of buffer) {
			this._handleAgentSignal(agent, signal);
		}
	}

	// ---- Subagent session management ----------------------------------------

	/**
	 * Creates a subagent session in response to a `subagent_started` event.
	 * The subagent session is created silently (no `sessionAdded` notification)
	 * and immediately transitioned to ready with an active turn.
	 */
	private _handleSubagentStarted(
		parentSession: ProtocolURI,
		toolCallId: string,
		agentName: string,
		agentDisplayName: string,
		agentDescription?: string,
	): void {
		const subagentSessionUri = buildSubagentSessionUri(parentSession, toolCallId);
		const subagentKey = `${parentSession}:${toolCallId}`;

		// Already tracking this subagent
		if (this._subagentSessions.has(subagentKey)) {
			return;
		}

		this._logService.info(`[AgentSideEffects] Creating subagent session: ${subagentSessionUri} (parent=${parentSession}, toolCallId=${toolCallId})`);
		const parentState = this._stateManager.getSessionState(parentSession);

		// Create the subagent session silently (restoreSession skips notification)
		this._stateManager.restoreSession(
			{
				resource: subagentSessionUri,
				provider: 'subagent',
				title: agentDisplayName,
				status: SessionStatus.Idle,
				createdAt: Date.now(),
				modifiedAt: Date.now(),
				...(parentState?.summary.project ? { project: parentState.summary.project } : {}),
			},
			[],
		);

		// Start a turn on the subagent session
		const turnId = generateUuid();
		this._stateManager.dispatchServerAction(subagentSessionUri, {
			type: ActionType.SessionTurnStarted,
			turnId,
			message: { text: '', origin: { kind: MessageKind.User } },
		});

		this._subagentSessions.set(subagentKey, subagentSessionUri);

		// Dispatch content on the parent tool call so clients discover the subagent.
		// Merge with any existing content to avoid dropping prior content blocks.
		const parentTurnId = this._stateManager.getActiveTurnId(parentSession);
		if (parentTurnId) {
			const parentState = this._stateManager.getSessionState(parentSession);
			const existingContent = this._getRunningToolCallContent(parentState, parentTurnId, toolCallId);
			const mergedContent = [
				...existingContent,
				{
					type: ToolResultContentType.Subagent as const,
					resource: subagentSessionUri,
					title: agentDisplayName,
					agentName,
					description: agentDescription,
				},
			];
			this._stateManager.dispatchServerAction(parentSession, {
				type: ActionType.SessionToolCallContentChanged,
				turnId: parentTurnId,
				toolCallId,
				content: mergedContent,
			});
		}
	}

	/**
	 * Gets the current content array from a running tool call, if any.
	 */
	private _getRunningToolCallContent(
		state: SessionState | undefined,
		turnId: string,
		toolCallId: string,
	): ToolResultContent[] {
		if (!state?.activeTurn || state.activeTurn.id !== turnId) {
			return [];
		}
		for (const rp of state.activeTurn.responseParts) {
			if (rp.kind === ResponsePartKind.ToolCall && rp.toolCall.toolCallId === toolCallId && rp.toolCall.status === ToolCallStatus.Running) {
				return rp.toolCall.content ? [...rp.toolCall.content] : [];
			}
		}
		return [];
	}

	/**
	 * Cancels all active subagent sessions for a given parent session.
	 */
	cancelSubagentSessions(parentSession: ProtocolURI): void {
		for (const [key, subagentUri] of this._subagentSessions) {
			if (key.startsWith(`${parentSession}:`)) {
				const turnId = this._stateManager.getActiveTurnId(subagentUri);
				if (turnId) {
					this._stateManager.dispatchServerAction(subagentUri, {
						type: ActionType.SessionTurnCancelled,
						turnId,
					});
					this._turnTracker.turnCompleted(subagentUri, turnId, 'cancelled');
				}
				this._subagentSessions.delete(key);
			}
		}
		// Drop any buffered events targeted at subagents that never started.
		for (const key of [...this._pendingSubagentSignals.keys()]) {
			if (key.startsWith(`${parentSession}:`)) {
				this._pendingSubagentSignals.delete(key);
			}
		}
	}

	/**
	 * Completes the subagent session associated with a parent tool call.
	 * Driven by the `subagent_completed` signal from the agent (which the
	 * SDK fires on both `subagent.completed` and `subagent.failed`), not by
	 * parent tool call completion — background subagents keep running after
	 * their parent tool returns.
	 */
	completeSubagentSession(parentSession: ProtocolURI, toolCallId: string): void {
		const key = `${parentSession}:${toolCallId}`;

		// Drop any events that were buffered waiting for a `subagent_started`
		// that never arrived (e.g. the parent tool failed before the subagent
		// was created). Without this, the buffer entry would leak until the
		// parent session is disposed.
		this._pendingSubagentSignals.delete(key);

		const subagentUri = this._subagentSessions.get(key);
		if (!subagentUri) {
			return;
		}

		const turnId = this._stateManager.getActiveTurnId(subagentUri);
		if (turnId) {
			this._stateManager.dispatchServerAction(subagentUri, {
				type: ActionType.SessionTurnComplete,
				turnId,
			});
		}
		this._subagentSessions.delete(key);
	}

	/**
	 * Removes all subagent sessions for a given parent session from
	 * the state manager. Called when the parent session is disposed.
	 */
	removeSubagentSessions(parentSession: ProtocolURI): void {
		const toRemove: string[] = [];
		for (const [key, subagentUri] of this._subagentSessions) {
			if (key.startsWith(`${parentSession}:`)) {
				this._stateManager.disposeSessionChangesets(subagentUri);
				this._stateManager.removeSession(subagentUri);
				toRemove.push(key);
			}
		}
		for (const key of toRemove) {
			this._subagentSessions.delete(key);
		}

		// Also clean up any subagent sessions that are in the state manager
		// but not tracked (e.g. restored sessions)
		const prefix = `${parentSession}/subagent/`;
		for (const uri of this._stateManager.getSessionUrisWithPrefix(prefix)) {
			this._stateManager.disposeSessionChangesets(uri);
			this._stateManager.removeSession(uri);
		}

		// Drop any buffered events targeted at subagents that never started.
		for (const key of [...this._pendingSubagentSignals.keys()]) {
			if (key.startsWith(`${parentSession}:`)) {
				this._pendingSubagentSignals.delete(key);
			}
		}
	}

	/**
	 * Finds the subagent session that owns a given tool call by checking
	 * whether the tool call was previously registered under a subagent
	 * session key in `_toolCallAgents`. Scoped to subagent sessions owned
	 * by the given parent to avoid cross-session collisions.
	 */
	private _findSubagentSessionForToolCall(parentSession: ProtocolURI, toolCallId: string): ProtocolURI | undefined {
		const prefix = `${parentSession}:`;
		for (const [key, subagentUri] of this._subagentSessions) {
			if (key.startsWith(prefix) && this._toolCallAgents.has(`${subagentUri}:${toolCallId}`)) {
				return subagentUri;
			}
		}
		return undefined;
	}

	// ---- Side-effect handlers --------------------------------------------------

	/**
	 * Handles a `pending_confirmation` signal end-to-end: checks for
	 * auto-approval via the permission manager, and if not auto-approved,
	 * dispatches the `SessionToolCallReady` action with confirmation options
	 * for the client.
	 */
	private _handleToolReady(e: IAgentToolPendingConfirmationSignal, sessionKey: ProtocolURI, turnId: string, agent: IAgent): void {
		const approvalEvent = {
			toolCallId: e.state.toolCallId,
			session: e.session,
			permissionKind: e.permissionKind,
			permissionPath: e.permissionPath,
			toolInput: e.state.toolInput,
		};
		const autoApproval = this._permissionManager.getAutoApproval(approvalEvent, sessionKey);
		const part = this._stateManager.getSessionState(sessionKey)?.activeTurn?.responseParts.find(part => part.kind === ResponsePartKind.ToolCall && part.toolCall.toolCallId === e.state.toolCallId);
		const toolCall = part?.kind === ResponsePartKind.ToolCall ? part.toolCall : undefined;
		const contributor = e.state.contributor ?? toolCall?.contributor;
		let effective = e;
		const clientShouldAutoApprove = autoApproval !== undefined
			&& contributor?.kind === ToolCallContributorKind.Client
			&& !!e.state.confirmationTitle;
		if (clientShouldAutoApprove) {
			this._toolCallAgents.set(`${sessionKey}:${e.state.toolCallId}`, agent.id);
			effective = { ...e, state: { ...e.state, _meta: { ...toolCall?._meta, ...e.state._meta, autoApproveBySetting: true } } };
		} else if (autoApproval !== undefined) {
			this._toolCallAgents.delete(`${sessionKey}:${e.state.toolCallId}`);
			agent.respondToPermissionRequest(e.state.toolCallId, true);
			// Strip confirmationTitle so createToolReadyAction emits the
			// auto-approved (no-options) action.
			effective = { ...e, state: { ...e.state, confirmationTitle: undefined } };
		} else if (effective.state.confirmationTitle) {
			// Make sure the agent is registered for the eventual `SessionToolCallConfirmed` response.
			this._toolCallAgents.set(`${sessionKey}:${e.state.toolCallId}`, agent.id);
		}
		this._stateManager.dispatchServerAction(
			sessionKey,
			this._permissionManager.createToolReadyAction(effective, sessionKey, turnId)
		);
	}

	handleAction(channel: ProtocolURI, action: StateAction): void {
		switch (action.type) {
			case ActionType.SessionTurnStarted: {
				// Per-turn streaming part tracking is owned by the agent
				// (e.g. CopilotAgentSession) and reset on its `send()` call.

				// `/rename [title]` is a generic, agent-agnostic slash command:
				// it is intercepted here and redirected to a title change rather
				// than forwarded to the agent SDK. Mirrors the per-agent text-side
				// dispatch (`parseLeadingSlashCommand` in CopilotAgentSession), but
				// applies to every session type.
				if (this._tryHandleRenameCommand(channel, action.turnId, action.message.text)) {
					break;
				}

				const state = this._stateManager.getSessionState(channel);
				this._titleController.seedTitleFromFirstMessage(channel, action.message.text);

				const agent = this._options.getAgent(channel);
				if (!agent) {
					this._stateManager.dispatchServerAction(channel, {
						type: ActionType.SessionError,
						turnId: action.turnId,
						error: { errorType: 'noAgent', message: 'No agent found for session' },
					});
					return;
				}
				const attachments = action.message.attachments;
				this._telemetryReporter.userMessageSent(agent.id, channel, state, 'direct', attachments);
				const { model, permissionLevel } = this._getTurnTelemetryContext(state);
				this._turnTracker.turnStarted(agent.id, channel, action.turnId, model, permissionLevel);
				agent.sendMessage(URI.parse(channel), action.message.text, attachments, action.turnId).catch(err => {
					const errCode = (err as { code?: number })?.code;
					this._logService.error(`[AgentSideEffects] sendMessage failed for session=${channel}: code=${errCode}, message=${err instanceof Error ? err.message : String(err)}, type=${err?.constructor?.name}`, err);
					this._stateManager.dispatchServerAction(channel, {
						type: ActionType.SessionError,
						turnId: action.turnId,
						error: buildSendFailedError(err),
					});
					this._turnTracker.turnCompleted(channel, action.turnId, 'error');
				});
				break;
			}
			case ActionType.SessionToolCallConfirmed: {
				const toolCallKey = `${channel}:${action.toolCallId}`;
				const agentId = this._toolCallAgents.get(toolCallKey);
				if (agentId) {
					this._toolCallAgents.delete(toolCallKey);
					const agent = this._options.agents.get().find(a => a.id === agentId);
					agent?.respondToPermissionRequest(action.toolCallId, action.approved);
				} else {
					this._logService.warn(`[AgentSideEffects] No agent for tool call confirmation: ${action.toolCallId}`);
				}

				// When the user chose "Allow in this Session", add the tool
				// to the session's permissions so future calls are auto-approved.
				if (action.approved) {
					this._permissionManager.handleToolCallConfirmed(channel, action.toolCallId, action.selectedOptionId);
				}
				break;
			}
			case ActionType.SessionInputCompleted: {
				const agent = this._options.getAgent(channel);
				agent?.respondToUserInputRequest(action.requestId, action.response, action.answers);
				break;
			}
			case ActionType.SessionTurnCancelled: {
				this._turnTracker.turnCompleted(channel, action.turnId, 'cancelled');
				// Cancel all subagent sessions for this parent
				this.cancelSubagentSessions(channel);
				const agent = this._options.getAgent(channel);
				agent?.abortSession(URI.parse(channel)).catch(err => {
					this._logService.error('[AgentSideEffects] abortSession failed', err);
				});
				break;
			}
			case ActionType.SessionModelChanged: {
				const agent = this._options.getAgent(channel);
				agent?.changeModel?.(URI.parse(channel), action.model).catch(err => {
					this._logService.error('[AgentSideEffects] changeModel failed', err);
				});
				break;
			}
			case ActionType.SessionAgentChanged: {
				const agent = this._options.getAgent(channel);
				agent?.changeAgent?.(URI.parse(channel), action.agent).catch(err => {
					this._logService.error('[AgentSideEffects] changeAgent failed', err);
				});
				break;
			}
			case ActionType.SessionTitleChanged: {
				this._persistSessionFlag(channel, 'customTitle', action.title);
				break;
			}
			case ActionType.SessionPendingMessageSet:
			case ActionType.SessionPendingMessageRemoved:
			case ActionType.SessionQueuedMessagesReordered: {
				this._syncPendingMessages(channel);
				break;
			}
			case ActionType.SessionTruncated: {
				const agent = this._options.getAgent(channel);
				agent?.truncateSession?.(URI.parse(channel), action.turnId).catch(err => {
					this._logService.error('[AgentSideEffects] truncateSession failed', err);
				});
				this._changesets.onSessionTruncated(channel);
				break;
			}
			case ActionType.SessionActiveClientChanged: {
				const agent = this._options.getAgent(channel);
				if (!agent) {
					break;
				}
				// Always forward client tools, even if empty, to clear previous client's tools
				const clientId = action.activeClient?.clientId;
				agent.setClientTools(URI.parse(channel), clientId, action.activeClient?.tools ?? []);

				const refs = action.activeClient?.customizations ?? [];
				agent.setClientCustomizations(URI.parse(channel), clientId ?? '', refs).catch(err => {
					this._logService.error('[AgentSideEffects] setClientCustomizations failed', err);
				});
				break;
			}
			case ActionType.RootConfigChanged: {
				updateAgentHostTelemetryLevelFromConfig(this._telemetryService, action.config);
				// Host customizations are self-managed by each agent's
				// PluginController via IAgentConfigurationService.onDidRootConfigChange.
				// Republish agent infos for non-customization schema changes
				// (e.g. permissions) and session customizations as a catchall.
				this._publishAgentInfos(this._options.agents.get());
				this._publishAllSessionCustomizations();
				break;
			}
			case ActionType.SessionActiveClientToolsChanged: {
				const agent = this._options.getAgent(channel);
				if (agent) {
					const sessionState = this._stateManager.getSessionState(channel);
					const toolClientId = sessionState?.activeClient?.clientId;
					if (toolClientId) {
						agent.setClientTools(URI.parse(channel), toolClientId, action.tools);
					}
				}
				break;
			}
			case ActionType.SessionCustomizationToggled: {
				const agent = this._options.getAgent(channel);
				agent?.setCustomizationEnabled?.(action.id, action.enabled);
				break;
			}
			case ActionType.SessionIsReadChanged: {
				this._persistSessionFlag(channel, 'isRead', action.isRead ? 'true' : '');
				break;
			}
			case ActionType.SessionIsArchivedChanged: {
				this._persistSessionFlag(channel, 'isArchived', action.isArchived ? 'true' : '');
				const agent = this._options.getAgent(channel);
				agent?.onArchivedChanged?.(URI.parse(channel), action.isArchived).catch(err => {
					this._logService.warn(`[AgentSideEffects] onArchivedChanged failed for ${channel}`, err);
				});
				break;
			}
			case ActionType.SessionConfigChanged: {
				// Persist merged values so a future `restoreSession` can re-hydrate
				// the user's previous selections (e.g. autoApprove).
				const sessionState = this._stateManager.getSessionState(channel);
				const values = sessionState?.config?.values;
				if (values) {
					this._persistSessionFlag(channel, 'configValues', JSON.stringify(values));
				}
				break;
			}
			case ActionType.SessionToolCallComplete: {
				const agent = this._options.getAgent(channel);
				agent?.onClientToolCallComplete(URI.parse(channel), action.toolCallId, action.result);
				break;
			}
		}
	}

	cancelSessionTitleGeneration(session: ProtocolURI): void {
		this._titleController.cancelTitleGeneration(session);
	}

	/**
	 * Handles the generic `/rename [title]` slash command. When `text` is a
	 * rename command it is redirected to a {@link ActionType.SessionTitleChanged}
	 * action (when a non-empty title is supplied) and the just-started turn is
	 * immediately completed, so the command is never forwarded to the agent SDK.
	 *
	 * @returns `true` when the message was a rename command and was handled here
	 * (the caller MUST NOT forward it to the agent), `false` otherwise.
	 */
	private _tryHandleRenameCommand(channel: ProtocolURI, turnId: string, text: string): boolean {
		const title = parseRenameCommand(text);
		if (title === undefined) {
			return false;
		}
		if (title.length > 0) {
			this._stateManager.dispatchServerAction(channel, {
				type: ActionType.SessionTitleChanged,
				title,
			});
			// Server-dispatched actions bypass `handleAction`, so persist the
			// new title here directly (the client-dispatched rename path relies
			// on the `SessionTitleChanged` case in `handleAction` instead).
			this._persistSessionFlag(channel, 'customTitle', title);
			// Acknowledge the rename with a brief response so the turn has
			// visible content in the transcript.
			this._stateManager.dispatchServerAction(channel, {
				type: ActionType.SessionResponsePart,
				turnId,
				part: {
					kind: ResponsePartKind.Markdown,
					id: generateUuid(),
					content: localize('agentHostRename.renamed', "Renamed: {0}", title),
				},
			});
		}
		// Close out the turn that the reducer opened for this message so the
		// session returns to idle instead of waiting on an agent response.
		this._stateManager.dispatchServerAction(channel, {
			type: ActionType.SessionTurnComplete,
			turnId,
		});
		// This turn was completed via a direct server dispatch rather than
		// `_runTurnCompleteSideEffects`, so drain any messages queued behind
		// the rename ourselves; otherwise they would stall until the next
		// unrelated state change re-triggers consumption.
		this._tryConsumeNextQueuedMessage(channel);
		return true;
	}

	/**
	 * Persists a session metadata key/value pair to the session database.
	 * Used for fields the host needs to remember across restarts (custom
	 * title, isRead/isArchived flags, merged config values).
	 *
	 * Counterpart in `agentHostChangesetService.ts` (`AgentHostChangesetService._persistSessionFlag`):
	 * keep both copies in sync if the signature changes. Duplicated rather
	 * than lifted because the two consumers persist disjoint metadata
	 * (changeset diffs there vs. customTitle / isRead / isArchived /
	 * configValues here) and a shared util would only have two callers.
	 */
	private _persistSessionFlag(session: ProtocolURI, key: string, value: string): void {
		const ref = this._options.sessionDataService.openDatabase(URI.parse(session));
		ref.object.setMetadata(key, value).catch(err => {
			this._logService.warn(`[AgentSideEffects] Failed to persist ${key}`, err);
		}).finally(() => {
			ref.dispose();
		});
	}

	/**
	 * Pushes the current pending message state from the session to the agent.
	 * The server controls queued message consumption; only steering messages
	 * are forwarded to the agent for mid-turn injection.
	 */
	private _syncPendingMessages(session: ProtocolURI): void {
		const state = this._stateManager.getSessionState(session);
		if (!state) {
			return;
		}
		const agent = this._options.getAgent(session);
		agent?.setPendingMessages?.(
			URI.parse(session),
			state.steeringMessage,
			[],
		);

		// Steering message removal is now dispatched by the agent
		// via the 'steering_consumed' progress event once the message
		// has actually been sent to the model.

		// If the session is idle, try to consume the next queued message
		this._tryConsumeNextQueuedMessage(session);
	}

	/**
	 * Consumes the next queued message by dispatching a server-initiated
	 * `SessionTurnStarted` action with `queuedMessageId` set. The reducer
	 * atomically creates the active turn and removes the message from the
	 * queue. Only consumes one message at a time; subsequent messages are
	 * consumed when the next `idle` event fires.
	 */
	private _tryConsumeNextQueuedMessage(session: ProtocolURI): void {
		// Bail if there's already an active turn
		if (this._stateManager.getActiveTurnId(session)) {
			return;
		}
		const state = this._stateManager.getSessionState(session);
		if (!state?.queuedMessages?.length) {
			return;
		}

		const msg = state.queuedMessages[0];
		const turnId = generateUuid();

		// Per-turn streaming part tracking is owned by the agent (reset
		// inside its `send()` call), so no host-side reset is needed.

		// Dispatch server-initiated turn start; the reducer removes the queued message atomically
		this._stateManager.dispatchServerAction(session, {
			type: ActionType.SessionTurnStarted,
			turnId,
			message: msg.message,
			queuedMessageId: msg.id,
		});

		// `/rename` is intercepted generically (see the SessionTurnStarted
		// handler) and must not reach the agent SDK even when queued.
		if (this._tryHandleRenameCommand(session, turnId, msg.message.text)) {
			return;
		}

		// Send the message to the agent backend
		const agent = this._options.getAgent(session);
		if (!agent) {
			this._stateManager.dispatchServerAction(session, {
				type: ActionType.SessionError,
				turnId,
				error: { errorType: 'noAgent', message: 'No agent found for session' },
			});
			return;
		}
		const attachments = msg.message.attachments;
		const queuedState = this._stateManager.getSessionState(session);
		this._telemetryReporter.userMessageSent(agent.id, session, queuedState, 'queued', attachments);
		const { model, permissionLevel } = this._getTurnTelemetryContext(queuedState);
		this._turnTracker.turnStarted(agent.id, session, turnId, model, permissionLevel);
		agent.sendMessage(URI.parse(session), msg.message.text, attachments, turnId).catch(err => {
			this._logService.error('[AgentSideEffects] sendMessage failed (queued)', err);
			this._stateManager.dispatchServerAction(session, {
				type: ActionType.SessionError,
				turnId,
				error: buildSendFailedError(err),
			});
			this._turnTracker.turnCompleted(session, turnId, 'error');
		});
	}


	private _getTurnTelemetryContext(state: SessionState | undefined): { model: string | undefined; permissionLevel: string | undefined } {
		const model = state?.summary.model?.id;
		const permissionValue = state?.config?.values[SessionConfigKey.AutoApprove];
		const permissionLevel = typeof permissionValue === 'string' ? permissionValue : undefined;
		return { model, permissionLevel };
	}


	override dispose(): void {
		this._toolCallAgents.clear();
		super.dispose();
	}
}

/**
 * Builds the {@link ErrorInfo} for a failed `sendMessage` rejection. When the
 * rejection text carries a `VSCODE_PROXY_ERROR` marker (embedded by a model
 * proxy and echoed back through the agent SDK), the decoded structured chat
 * error is attached to `_meta.chatError` so core can render a rich, localized
 * message. Otherwise the raw error message is used as-is.
 */
function buildSendFailedError(err: unknown): ErrorInfo {
	const message = String(err);
	const forwarded = tryParseForwardedChatError(err instanceof Error ? err.message : message);
	if (forwarded) {
		return { errorType: 'sendFailed', message: stripProxyErrorMarker(message), _meta: toChatErrorMeta(forwarded) };
	}
	return { errorType: 'sendFailed', message };
}
